import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'

interface ResendInviteDialogProps {
  isOpen: boolean
  onClose: () => void
  onSend: (customMessage: string) => Promise<void>
  memberEmail: string
  roomTemplate?: string | null
  playerName: string
}

export function ResendInviteDialog({
  isOpen,
  onClose,
  onSend,
  memberEmail,
  roomTemplate,
  playerName,
}: ResendInviteDialogProps) {
  const [emailContent, setEmailContent] = useState('')
  const [isSending, setIsSending] = useState(false)
  const { currentRoom } = useSelector((state: RootState) => state.room)
  const user = useSelector((state: RootState) => state.user.user)

  const replacePlaceholders = useCallback(
    (template: string): string => {
      const context: Record<string, string> = {
        player_name: playerName,
        room_name: currentRoom?.roomname || '',
        invite_code: currentRoom?.invite_code || '',
        professor_name: user?.name || '',
        professor_email: user?.email || '',
      }

      let result = template
      for (const [key, value] of Object.entries(context)) {
        const placeholder = `{${key}}`
        result = result.replace(
          new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
          value
        )
      }
      return result
    },
    [playerName, currentRoom, user]
  )

  useEffect(() => {
    if (isOpen) {
      const template = roomTemplate && roomTemplate.trim() ? roomTemplate : ''
      const initialContent = replacePlaceholders(template)
      setEmailContent(initialContent)
    }
  }, [isOpen, roomTemplate, replacePlaceholders])

  const handleSend = useCallback(async () => {
    setIsSending(true)
    try {
      await onSend(emailContent)
      onClose()
    } catch (error) {
      console.error('Failed to send invite:', error)
    } finally {
      setIsSending(false)
    }
  }, [emailContent, onSend, onClose])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>Resend Invitation Email</DialogTitle>
          <DialogDescription>
            Review and edit the email that will be sent to {memberEmail}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label htmlFor='email-content'>Email Content</Label>
            <Textarea
              id='email-content'
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              className='min-h-[300px] font-mono text-sm'
              rows={15}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isSending}>
            {isSending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
